import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Rocket } from 'lucide-react';

export const TOUR_KEY = 'restaff_onboarding_completed';
export const TOUR_RESTART_KEY = 'restaff_restart_tour';

const TOUR_STEPS = [
  {
    target: null,
    title: 'Welcome to ReStaff',
    description: 'This short tour will show you the main tools used to manage your workforce.',
    position: 'center',
  },
  {
    target: "[data-tour='timeclock']",
    title: 'Time Clock',
    description: 'Employees clock in and clock out here to track work hours.',
    position: 'right',
  },
  {
    target: "[data-tour='schedule']",
    title: 'Schedule',
    description: 'Managers assign weekly shifts and publish schedules for staff.',
    position: 'right',
  },
  {
    target: "[data-tour='shiftswaps']",
    title: 'Shift Swaps',
    description: 'Employees can request to swap shifts with coworkers.',
    position: 'right',
  },
  {
    target: "[data-tour='timesheets']",
    title: 'Timesheets',
    description: 'Review worked hours and export payroll summaries.',
    position: 'right',
  },
  {
    target: "[data-tour='reports']",
    title: 'Reports',
    description: 'Analyze labor data and staffing trends.',
    position: 'right',
  },
  {
    target: "[data-tour='insights']",
    title: 'Executive Insights',
    description: 'Monitor workforce efficiency and staffing risk indicators.',
    position: 'right',
  },
  {
    target: "[data-tour='documents']",
    title: 'Documents',
    description: 'Store internal company files and policies.',
    position: 'right',
  },
  {
    target: "[data-tour='staff']",
    title: 'Staff Management',
    description: 'Add employees, manage roles, and configure PIN access.',
    position: 'right',
  },
  {
    target: null,
    title: "You're all set!",
    description: "You're ready to start using ReStaff. Explore the sidebar to get started.",
    position: 'center',
    isFinal: true,
  },
];

const TOOLTIP_WIDTH = 280;

function getSpotlightRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  return el.getBoundingClientRect();
}

function calcTooltipPos(rect, position) {
  const GAP = 14;
  if (!rect || position === 'center') {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }
  if (position === 'right') {
    const left = Math.min(rect.right + GAP, window.innerWidth - TOOLTIP_WIDTH - 12);
    const top = Math.max(8, rect.top + rect.height / 2 - 70);
    return { top, left };
  }
  if (position === 'bottom') {
    return { top: rect.bottom + GAP, left: Math.max(8, rect.left) };
  }
  const right = window.innerWidth - rect.left + GAP;
  const top = Math.max(8, rect.top + rect.height / 2 - 70);
  return { top, right };
}

export function ProductTour() {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [spotRect, setSpotRect] = useState(null);

  const currentStep = TOUR_STEPS[step];

  const complete = useCallback(() => {
    localStorage.setItem(TOUR_KEY, 'true');
    localStorage.removeItem(TOUR_RESTART_KEY);
    setActive(false);
    setStep(0);
  }, []);

  // Update spotlight position on step change or window resize
  useEffect(() => {
    if (!active) return;
    const update = () => setSpotRect(getSpotlightRect(currentStep.target));
    update();
    // Retry once (sidebar may animate)
    const t = setTimeout(update, 300);
    window.addEventListener('resize', update);
    return () => { clearTimeout(t); window.removeEventListener('resize', update); };
  }, [active, step, currentStep.target]);

  // Auto-start on mount: new users or restart flag
  // Skip auto-start on small mobile screens (< 640px) for better UX
  useEffect(() => {
    const restart = localStorage.getItem(TOUR_RESTART_KEY) === 'true';
    const done = localStorage.getItem(TOUR_KEY) === 'true';
    const isMobile = window.innerWidth < 640;
    if (restart || (!done && !isMobile)) {
      const t = setTimeout(() => { setStep(0); setActive(true); }, 900);
      return () => clearTimeout(t);
    }
    // On mobile, mark as done automatically to prevent future auto-starts
    if (isMobile && !done) {
      localStorage.setItem(TOUR_KEY, 'true');
    }
  }, []);

  if (!active) return null;

  const isFirst = step === 0;
  const isLast = step === TOUR_STEPS.length - 1;
  const hasTarget = !!currentStep.target && !!spotRect;
  const tooltipPos = calcTooltipPos(spotRect, currentStep.position);

  const next = () => (isLast ? complete() : setStep(s => s + 1));
  const back = () => { if (!isFirst) setStep(s => s - 1); };

  return (
    <>
      {/* Click-to-dismiss backdrop */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9996, cursor: 'default' }}
        onClick={complete}
        data-testid="tour-backdrop"
      />

      {/* Dark overlay via large box-shadow on spotlight element */}
      {hasTarget ? (
        <div
          style={{
            position: 'fixed',
            top: spotRect.top - 5,
            left: spotRect.left - 5,
            width: spotRect.width + 10,
            height: spotRect.height + 10,
            borderRadius: 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.62)',
            zIndex: 9997,
            pointerEvents: 'none',
            transition: 'top 180ms ease, left 180ms ease, width 180ms ease, height 180ms ease',
          }}
          data-testid="tour-spotlight"
        />
      ) : (
        /* Full-screen dim when no target */
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.62)',
            zIndex: 9997, pointerEvents: 'none',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        style={{
          position: 'fixed',
          ...tooltipPos,
          zIndex: 9999,
          width: TOOLTIP_WIDTH,
          background: '#1e293b',
          color: '#f8fafc',
          borderRadius: 12,
          padding: '16px 18px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
          fontFamily: 'Manrope, sans-serif',
        }}
        onClick={e => e.stopPropagation()}
        data-testid="product-tour-tooltip"
      >
        {/* Progress + close */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {TOUR_STEPS.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === step ? 16 : 6, height: 6, borderRadius: 3,
                  background: i === step ? '#f97316' : i < step ? '#64748b' : '#334155',
                  transition: 'width 200ms, background 200ms',
                }}
              />
            ))}
          </div>
          <button
            onClick={complete}
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0 }}
            aria-label="Skip tour"
            data-testid="tour-close-btn"
          >
            <X size={14} />
          </button>
        </div>

        {currentStep.isFinal && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <Rocket size={28} color="#f97316" />
          </div>
        )}

        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, margin: '0 0 6px' }}>
          {currentStep.title}
        </h3>
        <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.55, margin: '0 0 14px' }}>
          {currentStep.description}
        </p>

        {/* Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={complete}
            style={{ background: 'none', border: 'none', color: '#475569', fontSize: 12, cursor: 'pointer', padding: 0 }}
            data-testid="tour-skip-btn"
          >
            Skip tour
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isFirst && (
              <button
                onClick={back}
                style={{
                  background: '#334155', border: 'none', color: '#e2e8f0',
                  borderRadius: 8, padding: '6px 12px', fontSize: 12,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}
                data-testid="tour-back-btn"
              >
                <ChevronLeft size={12} /> Back
              </button>
            )}
            <button
              onClick={next}
              style={{
                background: '#f97316', border: 'none', color: 'white',
                borderRadius: 8, padding: '6px 14px', fontSize: 12,
                fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
              data-testid="tour-next-btn"
            >
              {isLast ? 'Finish' : <><span>Next</span><ChevronRight size={12} /></>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/** Call this from outside (e.g. HelpCenter) to restart the tour */
export function triggerTourRestart() {
  localStorage.removeItem(TOUR_KEY);
  localStorage.setItem(TOUR_RESTART_KEY, 'true');
}
